import { debounce } from 'lodash';
import { RefObject, useLayoutEffect, useRef, useState } from 'react';
import { CatalogListCombination } from '../utils/catalogListCombinations.utils';
import { CATALOG_LIST_CHIPS_GAP } from '../utils/catalogListRow.consts';

export const CATALOG_LIST_COMBINATION_CHIP_ATTRIBUTE = 'data-combination-key';

const RESIZE_DEBOUNCE = 100;

const MORE_BUTTON_WIDTH = 72;

interface ArrangeForCountParams<Item> {
  items: Item[];
  selectedIndex: number;
  count: number;
}

const arrangeForCount = <Item>({ items, selectedIndex, count }: ArrangeForCountParams<Item>) => {
  const kept = items.slice(0, count);

  if (selectedIndex < count) {
    return kept;
  }

  return [...kept.slice(0, -1), items[selectedIndex]];
};

interface GetVisibleCountParams {
  containerWidth: number;
  chipWidths: number[];
  selectedIndex: number;
}

const getStripWidth = (widths: number[]) =>
  widths.reduce((total, width) => total + width + CATALOG_LIST_CHIPS_GAP, -CATALOG_LIST_CHIPS_GAP);

const getVisibleCount = ({ containerWidth, chipWidths, selectedIndex }: GetVisibleCountParams) => {
  const total = chipWidths.length;

  for (let count = total; count > 1; count -= 1) {
    const stripWidth = getStripWidth(arrangeForCount({ items: chipWidths, selectedIndex, count }));
    const budget =
      count === total
        ? containerWidth
        : containerWidth - CATALOG_LIST_CHIPS_GAP - MORE_BUTTON_WIDTH;

    if (stripWidth <= budget) {
      return count;
    }
  }

  return 1;
};

export interface CatalogListCombinationStrip {
  containerRef: RefObject<HTMLDivElement>;
  visible: CatalogListCombination[];
  overflow: CatalogListCombination[];
  isStacked: boolean;
}

interface UseCatalogListCombinationOverflowParams {
  combinations: CatalogListCombination[];
  selectedKey: string;
  isStacked: boolean;
}

const useCatalogListCombinationOverflow = ({
  combinations,
  selectedKey,
  isStacked,
}: UseCatalogListCombinationOverflowParams): CatalogListCombinationStrip => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chipWidthsRef = useRef(new Map<string, number>());
  const [visibleCount, setVisibleCount] = useState(combinations.length);
  const [measuredChipKeys, setMeasuredChipKeys] = useState<string | null>(null);

  const chipKeys = combinations.map((combination) => combination.combinationKey).join('|');
  const selectedIndex = combinations.findIndex(
    (combination) => combination.combinationKey === selectedKey,
  );

  useLayoutEffect(() => {
    const container = containerRef.current;

    if (!container || isStacked || !chipKeys) {
      return;
    }

    const keys = chipKeys.split('|');

    const recalculate = () => {
      const chipWidths = chipWidthsRef.current;

      chipWidths.forEach((_width, key) => {
        if (!keys.includes(key)) {
          chipWidths.delete(key);
        }
      });

      container.querySelectorAll(`[${CATALOG_LIST_COMBINATION_CHIP_ATTRIBUTE}]`).forEach((chip) => {
        const key = chip.getAttribute(CATALOG_LIST_COMBINATION_CHIP_ATTRIBUTE);

        if (key) {
          chipWidths.set(key, chip.getBoundingClientRect().width);
        }
      });

      const containerWidth = container.getBoundingClientRect().width;
      const measuredWidths = keys.map((key) => chipWidths.get(key) ?? 0);

      if (containerWidth <= 0 || measuredWidths.includes(0)) {
        return;
      }

      setVisibleCount(
        getVisibleCount({
          containerWidth,
          selectedIndex: keys.indexOf(selectedKey),
          chipWidths: measuredWidths,
        }),
      );
      setMeasuredChipKeys(chipKeys);
    };

    recalculate();

    const recalculateDebounced = debounce(recalculate, RESIZE_DEBOUNCE);
    const observer = new ResizeObserver(recalculateDebounced);
    observer.observe(container);

    return () => {
      observer.disconnect();
      recalculateDebounced.cancel();
    };
  }, [chipKeys, selectedKey, isStacked]);

  const isMeasured = measuredChipKeys === chipKeys;
  const visibleLimit =
    isStacked || !isMeasured ? combinations.length : Math.min(visibleCount, combinations.length);

  const visible = arrangeForCount({ items: combinations, selectedIndex, count: visibleLimit });

  return {
    containerRef,
    visible,
    overflow: combinations.filter((combination) => !visible.includes(combination)),
    isStacked,
  };
};

export default useCatalogListCombinationOverflow;
